/**
 * ASTRIXY.ONLINE marketplace Meta Pixel (browser) + CAPI mirror.
 *
 * The pixel ID is not bundled: it is fetched at runtime from the public
 * config endpoint (/api/public/marketplace/pixel-config), which reads the
 * MARKETPLACE_META_PIXEL_ID secret server-side. Pixel IDs are public by
 * nature (they ship in every page's JS), so exposing it is safe.
 *
 * Everything is gated on marketing consent. CAPI mirrors share the eventId
 * with the browser event so Meta dedupes the pair.
 */
import { isAllowed } from '@/lib/consent/manager';

let pixelId: string | null | undefined; // undefined = not fetched yet
let loadStarted = false;

const marketingAllowed = (): boolean => {
  try {
    return isAllowed('marketing');
  } catch {
    return false;
  }
};

function loadFbqScript(id: string): void {
  if (loadStarted) return;
  loadStarted = true;
  const w = window as unknown as { fbq?: unknown; _fbq?: unknown };
  if (typeof w.fbq !== 'function') {
    /* eslint-disable */
    (function (f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = true; t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
  }
  window.fbq!('init', id);
}

/** Fetch the marketplace pixel ID once and init fbq when consent allows. */
export async function initMarketplacePixel(): Promise<void> {
  if (typeof window === 'undefined' || !marketingAllowed()) return;
  if (pixelId === undefined) {
    try {
      const res = await fetch('/api/public/marketplace/pixel-config');
      const json = (await res.json()) as { pixelId?: string };
      pixelId = typeof json.pixelId === 'string' && json.pixelId ? json.pixelId : null;
    } catch {
      pixelId = null;
    }
  }
  if (!pixelId) return;
  loadFbqScript(pixelId);
}

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : undefined;
};

type MpEvent = 'PageView' | 'ViewContent' | 'InitiateCheckout' | 'Purchase';

/** Fire one event on the browser pixel and mirror it through CAPI. */
export function mpMetaTrack(
  eventName: MpEvent,
  eventId: string,
  extra: { value?: number; currency?: string; contentName?: string; email?: string } = {},
): void {
  if (typeof window === 'undefined' || !marketingAllowed()) return;

  try {
    const params: Record<string, unknown> = {};
    if (extra.value !== undefined) params.value = extra.value;
    if (extra.currency) params.currency = extra.currency;
    if (extra.contentName) params.content_name = extra.contentName;
    window.fbq?.('track', eventName, params, { eventID: eventId });
  } catch {
    // Tracking must never break the app.
  }

  void (async () => {
    try {
      const { sendMpCapiEvent, mintMpCapiEventToken } = await import(
        '@/lib/marketplace/mpMetaCapi.functions'
      );
      const minted = await mintMpCapiEventToken();
      if (!minted.ok) return;
      await sendMpCapiEvent({
        data: {
          eventName,
          eventId,
          token: minted.token,
          eventSourceUrl: window.location.href,
          ...(extra.value !== undefined ? { value: extra.value } : {}),
          ...(extra.currency ? { currency: extra.currency } : {}),
          ...(extra.contentName ? { contentName: extra.contentName } : {}),
          ...(extra.email ? { email: extra.email } : {}),
          ...(readCookie('_fbp') ? { fbp: readCookie('_fbp') } : {}),
          ...(readCookie('_fbc') ? { fbc: readCookie('_fbc') } : {}),
        },
      });
    } catch {
      // Tracking must never break the app.
    }
  })();
}
